
import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  IconButton,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import { useAuth } from 'contexts/authContext';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { contractorRepository } from 'api/contractorRepository';

export default function AddContractorForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useAuth();
  const contractorId = location.state?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractorData, setContractorData] = useState({
    name: '',
    contactEmail: '',
    contactNumber: '',
    status: 'Active',
    address: {
      locationName: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
  });

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] =
    useState<'success' | 'info' | 'warning' | 'error'>('success');

  const centerAlertTimerRef = useRef<number | null>(null);

  const showCenteredAlert = (
    msg: string,
    status: 'success' | 'info' | 'warning' | 'error' = 'success',
    duration = 3000
  ) => {
    if (centerAlertTimerRef.current) {
      window.clearTimeout(centerAlertTimerRef.current);
    }

    setCenterAlertMessage(msg);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);

    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
    }, duration);
  };

  useEffect(() => {
    if (!contractorId) return;

    const fetchData = async () => {
      try {
        const res = await contractorRepository.getContractorById(contractorId);

        setContractorData({
          name: res.name || '',
          contactEmail: res.contactEmail || '',
          contactNumber: res.contactNumber || '',
          status: res.status || 'Active',
          address: {
            locationName: res.address?.locationName || '',
            street: res.address?.street || '',
            city: res.address?.city || '',
            state: res.address?.state || '',
            zipCode: res.address?.zipCode || '',
            country: res.address?.country || '',
          },
        });
      } catch (error) {
        showCenteredAlert('Email already exists in Contractor', 'error');
      }
    };

    fetchData();
  }, [contractorId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setContractorData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContractorData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = {
        ...contractorData,
        orgId: orgId,
      };

      if (contractorId) {
        await contractorRepository.updateContractor(contractorId, payload);
        showCenteredAlert('Contractor updated successfully!', 'success');
      } else {
        await contractorRepository.addContractor(payload);
        showCenteredAlert('Contractor Added Successfully!', 'success');
      }

      setTimeout(() => {
        navigate('/admin/organizations/ContractorsList');
      }, 1500);
    } catch (error: any) {
      showCenteredAlert('Failed to process contractor', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="center"
      position="relative"
      px={{ base: '20px', md: '40px' }}
      pb="60px"
    >
      <Box
        position="absolute"
        top="90"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        p="30px"
        mt={{ md: '150px' }}
        zIndex={1}
        borderRadius="16px"
      >
        <Flex justify="space-between" align="center" mb="20px">
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            {contractorId ? 'Edit Contractor' : 'Add Contractor'}
          </Text>

          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/organizations/ContractorsList')}
          />
        </Flex>

        {/* BASIC DETAILS */}
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
          <Stack spacing="20px">
            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Contractor Name
              </Text>
              <InputField
                name="name"
                placeholder="Enter contractor name"
                value={contractorData.name}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Contact Email
              </Text>
              <InputField
                name="contactEmail"
                type="email"
                placeholder="Enter contact email"
                value={contractorData.contactEmail}
                onChange={handleInputChange}
              />
            </FormControl>
          </Stack>

          <Stack spacing="20px">
            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Contact Number
              </Text>
              <InputField
                name="contactNumber"
                placeholder="Enter contact number"
                value={contractorData.contactNumber}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Status
              </Text>
              <Select value={contractorData.status} isDisabled>
                <option>Active</option>
              </Select>
            </FormControl>
          </Stack>
        </SimpleGrid>

        {/* ADDRESS DETAILS */}
        <Box mt="20px">
          <Text fontSize="lg" fontWeight="700" mb="12px" color={textColor}>
            Address Details
          </Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Address Line
              </Text>
              <InputField
                name="locationName"
                placeholder="Address Line"
                value={contractorData.address.locationName}
                onChange={handleAddressChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Street
              </Text>
              <InputField
                name="street"
                placeholder="Street"
                value={contractorData.address.street}
                onChange={handleAddressChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                City
              </Text>
              <InputField
                name="city"
                placeholder="City"
                value={contractorData.address.city}
                onChange={handleAddressChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                State
              </Text>
              <InputField
                name="state"
                placeholder="State"
                value={contractorData.address.state}
                onChange={handleAddressChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Zip Code
              </Text>
              <InputField
                name="zipCode"
                placeholder="Zip Code"
                value={contractorData.address.zipCode}
                onChange={handleAddressChange}
              />
            </FormControl>

            <FormControl>
              <Text fontSize="sm" fontWeight="bold" mb="8px">
                Country
              </Text>
              <InputField
                name="country"
                placeholder="Country"
                value={contractorData.address.country}
                onChange={handleAddressChange}
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Flex justify="center" mt="24px">
          <Button
            colorScheme="blue"
            w="150px"
            h="45px"
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            {contractorId ? 'Update' : 'Submit'}
          </Button>
        </Flex>
      </Card>

      {/* ALERT */}
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}